import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { apolloClient } from '@/vendor/apollo-client.ts'
import { BACKGROUND_OPERATION } from '@/utils/backgroundOperation'
import { createSyncEngine } from '@/stores/sync/createSyncEngine.ts'
import { createPersistedCache } from '@/stores/sync/createPersistedCache.ts'
import { newClientId } from '@/stores/sync/clientIdentity.ts'
import { useInfoStore } from '@/stores/info.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { useLocalizationStore, type Language } from '@/stores/localization.ts'
import { showToast } from '@/utils/showToast.ts'
import { sendError } from '@/utils/sendError.ts'
import { GET_USER_LANGUAGE } from '@/queries/userLanguage.ts'
import { GET_USER_NOTIFICATION_SETTINGS } from '@/queries/notificationSettings.ts'
import { GET_PROFILE } from '@/queries/profile.ts'
import { GET_USER_NOTIFICATION_TOKENS } from '@/queries'
import {
  applyActionOverlay,
  DEFAULT_USER_SETTINGS,
  performSettingsSync,
  type NotificationSettings,
  type UserSettingsAction,
  type UserSettingsState,
} from './sync.ts'

export type { NotificationSettings, UserSettingsState } from './sync.ts'

const USER_SETTINGS_KEY = 'userSettingsState'
// The pending queue is persisted so offline settings edits survive an app restart and
// reach the server once back online (mirrors user-collection / user-lists).
const USER_SETTINGS_ACTIONS_KEY = 'userSettingsActionStack'

const cache = createPersistedCache<UserSettingsState>(USER_SETTINGS_KEY, DEFAULT_USER_SETTINGS)
const actionStackCache = createPersistedCache<UserSettingsAction[]>(USER_SETTINGS_ACTIONS_KEY, [])

function toLanguage(value: string | null | undefined): Language {
  return value === 'en' || value === 'de' ? value : DEFAULT_USER_SETTINGS.language
}

export const useUserSettingsStore = defineStore('user-settings', () => {
  const { connected } = storeToRefs(useInfoStore())
  const authStore = useAuthStore()
  const { t } = useLocalizationStore()

  const settingsData = ref<UserSettingsState>({ ...DEFAULT_USER_SETTINGS })

  function persist() {
    void cache.save(settingsData.value)
  }

  const engine = createSyncEngine<UserSettingsAction>({
    performSync: performSettingsSync,
    // A confirmed edit is folded into the base snapshot and its action retired, so the
    // overlay stays correct without waiting for a reload.
    onSynced: (action) => {
      if (action.kind === 'patch') {
        settingsData.value = { ...settingsData.value, ...action.patch }
      } else if (action.resultTokens !== undefined) {
        settingsData.value = { ...settingsData.value, notificationFcmTokens: action.resultTokens }
      }
      persist()
      engine.remove(action.id)
    },
    onError: (action, error) => {
      sendError(error, { source: 'user-settings.sync', kind: action.kind })
      void showToast({ text: t('notificationSettingsSaveError'), duration: 'short' })
    },
    // Keep edits queued offline instead of draining them into a failing call (which
    // onError drops). Resumed once connectivity returns.
    canSync: () => connected.value,
    onChange: (actions) => void actionStackCache.save(actions),
  })

  watch(connected, (isConnected) => {
    if (isConnected) engine.resume()
  })

  const settings = computed<UserSettingsState>(() =>
    applyActionOverlay(settingsData.value, engine.actionStack.value),
  )

  async function loadSettings() {
    if (!connected.value) return
    const [languageResult, notificationResult, tokenResult, profileResult] = await Promise.all([
      apolloClient.query({
        query: GET_USER_LANGUAGE,
        fetchPolicy: 'network-only',
        context: BACKGROUND_OPERATION,
      }),
      apolloClient.query({
        query: GET_USER_NOTIFICATION_SETTINGS,
        fetchPolicy: 'network-only',
        context: BACKGROUND_OPERATION,
      }),
      apolloClient.query({
        query: GET_USER_NOTIFICATION_TOKENS,
        fetchPolicy: 'network-only',
        context: BACKGROUND_OPERATION,
      }),
      apolloClient.query({
        query: GET_PROFILE,
        fetchPolicy: 'network-only',
        context: BACKGROUND_OPERATION,
      }),
    ])
    const notification = notificationResult.data?.users?.[0]
    const profile = profileResult.data?.users?.[0]
    settingsData.value = {
      language: toLanguage(languageResult.data?.users?.[0]?.language),
      notificationFcmTokens: tokenResult.data?.users?.[0]?.notificationFcmTokens ?? '',
      name: profile?.name ?? '',
      email: profile?.email ?? '',
      createdAt: profile?.admin?.createdAt ?? null,
      notificationNewReleasesFromPublishers:
        notification?.notificationNewReleasesFromPublishers ?? false,
      notificationNewReleasesFromPublishersList:
        notification?.notificationNewReleasesFromPublishersList ?? '',
      notificationNewReleasesFromAuthors: notification?.notificationNewReleasesFromAuthors ?? false,
      notificationNewReleasesFromAuthorsList:
        notification?.notificationNewReleasesFromAuthorsList ?? '',
      notificationAddedReleaseDateToExistingReleaseCollectionItem:
        notification?.notificationAddedReleaseDateToExistingReleaseCollectionItem ?? true,
      notificationChangedReleaseDateOfExistingReleaseCollectionItem:
        notification?.notificationChangedReleaseDateOfExistingReleaseCollectionItem ?? true,
      notificationReleaseCollectionItemAvailable:
        notification?.notificationReleaseCollectionItemAvailable ?? true,
      notificationReleaseCollectionItemAlternativeVersionRelease:
        notification?.notificationReleaseCollectionItemAlternativeVersionRelease ?? true,
      notificationScannedItemAvailable: notification?.notificationScannedItemAvailable ?? true,
      notificationAtsumeRecommendations: notification?.notificationAtsumeRecommendations ?? true,
    }
    persist()
  }

  const init = async () => {
    settingsData.value = await cache.load()
    // Restore the pending queue before any base load; replace() re-drains it (gated by
    // canSync, so offline it just stays queued).
    engine.replace(await actionStackCache.load())
    if (authStore.authState?.userAdmin?.user?.id) {
      await loadSettings()
    }
  }

  function updateNotificationSetting(patch: Partial<NotificationSettings>) {
    engine.enqueue({ id: newClientId(), synced: false, kind: 'patch', patch })
  }

  function setLanguage(language: Language) {
    engine.enqueue({ id: newClientId(), synced: false, kind: 'patch', patch: { language } })
  }

  function addFcmToken(token: string) {
    if (settings.value.notificationFcmTokens.split(',').includes(token)) return
    engine.enqueue({ id: newClientId(), synced: false, kind: 'fcm', op: 'add', token })
  }

  function removeFcmToken(token: string) {
    engine.enqueue({ id: newClientId(), synced: false, kind: 'fcm', op: 'remove', token })
  }

  return {
    init,
    loadSettings,
    settings,
    updateNotificationSetting,
    setLanguage,
    addFcmToken,
    removeFcmToken,
  }
})
